import { Analytics } from '@analytics';
import RFIDIcon from '@assets/images/RFID.svg';
import QRLoadingIcon from '@assets/images/qrLoading.svg';
import TextIconInformationBox from '@components/Onboarding/TextIconInformationBox';
import { TextIconBoxType } from '@components/Onboarding/constants';
import { StyleBreakpoints } from '@constants';
import {
  Button,
  CircularProgress,
  InputAdornment,
  OutlinedInput,
  TextField,
  Select
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { formatRegionalLinks } from '@utils/helpers';
import { QRCodeCanvas } from 'qrcode.react';
import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { currencySign } from '../../../utils/helpers';
import CustomizableModal from '../../Common/CustomizableModal';
import messages from './messages';

const useStyles = makeStyles(theme => ({
  input: {
    width: '100%',
    '& .MuiInputBase-root': {
      flexWrap: 'wrap'
    },
    '&:not(:first-child)': {
      marginTop: '16px'
    },
    '& .MuiInputBase-input': {
      fontSize: '16px',
      fontWeight: '600',
      color: '#5a5a5a'
    },
    '& .MuiInputLabel-root': {
      fontSize: '16px',
      fontWeight: '600',
      color: '#5a5a5a'
    }
  },
  inputAdornment: {
    '& .MuiTypography-root': {
      fontSize: '16px',
      fontWeight: '700',
      color: '#5a5a5a'
    },
    '& .MuiSelect-root': {
      width: '60px'
    }
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: 220,
    height: 220,
    [theme.breakpoints.up(StyleBreakpoints.xs)]: {
      width: 240,
      height: 240
    },
    [theme.breakpoints.up(StyleBreakpoints.sm)]: {
      width: 260,
      height: 260
    }
  }
}));

const PaymentLinkQRGeneratorModal = ({ isOpen, setIsOpen, currency, userId, companyName }) => {
  const intl = useIntl();
  const classes = useStyles();
  const qrGenerationTimeout = useRef(null);
  const [tag, setTag] = useState('');
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [homeCurrency, setHomeCurrency] = useState('');
  const [qrCurrency, setQRCurrency] = useState(currency)
  const [paymentLinkUrl, setPaymentLinkUrl] = useState(
    formatRegionalLinks(`p/g/${userId}?l=true&ti=false`)
  );
  const [copied, setCopied] = useState(false);
  const supportsShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  function handleClose() {
    setIsOpen(false);
  }

  useEffect(() => {
    if (qrGenerationTimeout.current) {
      clearTimeout(qrGenerationTimeout.current);
    }

    qrGenerationTimeout.current = setTimeout(() => {
      handleQRGeneration();
    }, 1000);
  }, [tag, paymentAmount, qrCurrency, homeCurrency]);

  // Trim digits after decimal place to prevent generating invalid amounts
  const trimAmount = amount => {
    const amountSections = amount.split('.');

    return `${amountSections[0]}${amountSections[1] ? `.${amountSections[1].substring(0, 2)}` : ''
      }`;
  };

  useEffect(() => {
    if (isOpen) {
      Analytics.trackViewFeature('PaymentLink');
    }
  }, [isOpen]);

  const handleQRGeneration = () => {
    // Don't generate QR until user selects a country
    if (!homeCurrency) {
      setIsLoading(false);
      return;
    }

    if (isOpen) {
      Analytics.track('generated new payment link qr code');
    }

    // Using URI encoding to preserve special characters used in tag in url
    setPaymentLinkUrl(
      formatRegionalLinks(
        `p/g/${userId}?l=true&currency=${qrCurrency}&ti=false${paymentAmount ? `&v=${paymentAmount}` : ''}${tag ? `&t=${encodeURIComponent(tag)}` : ''
        }${homeCurrency && homeCurrency !== 'other' ? `&homeCurrency=${homeCurrency}` : ''}`
      )
    );

    setIsLoading(false);
  };

  // Timeout after input to prevent repeatedly re-generating qr code
  const handleInputChange = (e, input) => {
    setIsLoading(true);

    Analytics.trackUseFeature('PaymentLink');

    if (input === 'tag') setTag(e.target.value);
    if (input === 'paymentAmount') setPaymentAmount(trimAmount(e.target.value));
  };

  const handleCurrencyChange = (e, input) => {
    setIsLoading(true);
    setQRCurrency(e.target.value);
  };

  const handleHomeCurrencyChange = (e) => {
    setIsLoading(true);
    setHomeCurrency(e.target.value);
  };

  const imageSettings = {
    height:
      window.innerWidth <= StyleBreakpoints.xs
        ? 220
        : window.innerWidth <= StyleBreakpoints.sm
          ? 240
          : 260,
    width:
      window.innerWidth <= StyleBreakpoints.xs
        ? 220
        : window.innerWidth <= StyleBreakpoints.sm
          ? 240
          : 260,
    x: 0,
    y: 0
  };

  const size =
    window.innerWidth <= StyleBreakpoints.xs
      ? 220
      : window.innerWidth <= StyleBreakpoints.sm
        ? 240
        : 260;

  // Title and logo live in the modal header row (next to the close button)
  // rather than stacked in the body, so the dialog fits a 1080p screen
  // without scrolling.
  const getModalTitle = () => {
    return (
      <div className="flex items-center gap-3">
        <img className="h-9 w-9" src={RFIDIcon} alt="" />
        <div>
          <div className="text-xl font-bold text-slate-800 tracking-tight leading-tight">
            {intl.formatMessage(messages.paymentLink)}
          </div>
          <div className="text-sm font-medium text-slate-500 leading-tight">
            {intl.formatMessage(messages.tagAndAmount)}
          </div>
        </div>
      </div>
    );
  };

  const getModalBody = () => {
    return (
      <div className="flex flex-col items-center">
        <div className="mb-4 max-w-[490px]">
          <div className="w-full">
            <div className="mb-2 text-xs font-semibold text-slate-500">
              Country of Customer
            </div>
            <div className="flex gap-3 flex-wrap">
              <Button
                variant={homeCurrency === 'usd' ? 'contained' : 'outlined'}
                color={homeCurrency === 'usd' ? 'primary' : 'default'}
                onClick={() => handleHomeCurrencyChange({ target: { value: 'usd' } })}
                className={`flex-[1_1_auto] !min-w-[120px] !normal-case !font-semibold !rounded-xl !px-5 !py-2.5 ${
                  homeCurrency === 'usd'
                    ? '!bg-indigo-600 hover:!bg-indigo-700 !text-white !shadow-sm'
                    : '!border-solid !border-slate-300 !text-slate-700'
                }`}
              >
                🇺🇸 USA
              </Button>
              <Button
                variant={homeCurrency === 'aud' ? 'contained' : 'outlined'}
                color={homeCurrency === 'aud' ? 'primary' : 'default'}
                onClick={() => handleHomeCurrencyChange({ target: { value: 'aud' } })}
                className={`flex-[1_1_auto] !min-w-[120px] !normal-case !font-semibold !rounded-xl !px-5 !py-2.5 ${
                  homeCurrency === 'aud'
                    ? '!bg-indigo-600 hover:!bg-indigo-700 !text-white !shadow-sm'
                    : '!border-solid !border-slate-300 !text-slate-700'
                }`}
              >
                🇦🇺 Australia
              </Button>
              <Button
                variant={homeCurrency === 'other' ? 'contained' : 'outlined'}
                color={homeCurrency === 'other' ? 'primary' : 'default'}
                onClick={() => handleHomeCurrencyChange({ target: { value: 'other' } })}
                className={`flex-[1_1_auto] !min-w-[120px] !normal-case !font-semibold !rounded-xl !px-5 !py-2.5 ${
                  homeCurrency === 'other'
                    ? '!bg-indigo-600 hover:!bg-indigo-700 !text-white !shadow-sm'
                    : '!border-solid !border-slate-300 !text-slate-700'
                }`}
              >
                🌍 Other
              </Button>
            </div>
          </div>
          <TextField
            multiline
            className={classes.input}
            label={intl.formatMessage(messages.tagRequired)}
            variant="outlined"
            value={tag}
            onChange={e => handleInputChange(e, 'tag')}
          />
          <OutlinedInput
            className={classes.input}
            startAdornment={
              <InputAdornment className={classes.inputAdornment} sx={{ fontSize: '16px' }}>
                <Select
                  native
                  onChange={e => handleCurrencyChange(e)}
                  value={qrCurrency}
                  inputProps={{
                    name: 'qrcurrency',
                    id: 'qrcurrency-native-simple',
                  }}
                >
                  <option value={'eur'}>{currencySign('eur')}</option>
                  <option value={'gbp'}>{currencySign('gbp')}</option>
                  <option value={'usd'}>{currencySign('usd')}</option>
                </Select>
              </InputAdornment>
            }
            variant="outlined"
            type="number"
            value={paymentAmount}
            inputProps={{
              inputMode: 'decimal',
              pattern: '[0-9]*[.,]?[0-9]*',
              step: 1,
              style: { textAlign: 'center' }
            }}
            onFocus={(e) => {
              // Clear the value when user focuses on the input
              setPaymentAmount('');
              e.target.select();
            }}
            onChange={e => handleInputChange(e, 'paymentAmount')}
          />

        </div>
        <div className="flex flex-col items-center w-full rounded-3xl bg-slate-50 border border-solid border-slate-200 px-4 py-4 gap-4 max-w-[490px]">
          <div className="flex flex-col justify-center items-center m-0 p-2.5 max-w-[340px] bg-white rounded-2xl border border-solid border-slate-200 shadow-sm">
            <div className="flex justify-center items-center p-2.5 rounded">
              {isLoading || !homeCurrency || !paymentAmount || paymentAmount === 0 ? (
                <div className={classes.loading}>
                  <img src={QRLoadingIcon} alt="Loading QR Code" className="w-full h-full" />
                </div>
              ) : (
                <QRCodeCanvas value={paymentLinkUrl} size={size} imageSettings={imageSettings} />
              )}
            </div>
          </div>
          <div className="max-w-[340px]">
            <TextIconInformationBox
              card={TextIconBoxType.PaymentLinkQR}
              noBackgroundCircle={true}
            />
          </div>
          <div className="w-full flex flex-row justify-center">
            {supportsShare ? (
              <Button
                className="w-full !max-w-[340px] !bg-indigo-600 hover:!bg-indigo-700 !text-white !normal-case !font-semibold !rounded-xl !shadow-sm disabled:!bg-slate-300 disabled:!text-white"
                variant="contained"
                color="primary"
                disabled={!homeCurrency || !paymentAmount || paymentAmount === 0}
                onClick={() => {
                  Analytics.track('share direct payment link clicked');

                  navigator.share({
                    title: `${companyName} payment`,
                    url: paymentLinkUrl
                  });
                }}>
                {intl.formatMessage(messages.shareLink)}
              </Button>
            ) : (
              <div className="w-full max-w-[340px]">
                <div className="flex gap-2 items-center">
                  <TextField
                    value={!homeCurrency || !paymentAmount || paymentAmount === 0 ? 'Please fill in the form above first' : paymentLinkUrl}
                    variant="outlined"
                    size="small"
                    fullWidth
                    InputProps={{
                      readOnly: true,
                      style: { fontSize: '12px' }
                    }}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={!homeCurrency || !paymentAmount || paymentAmount === 0}
                    onClick={() => {
                      Analytics.track('copy direct payment link clicked');
                      navigator.clipboard.writeText(paymentLinkUrl).then(() => {
                        setCopied(true);
                        setTimeout(() => setCopied(false), 2000);
                      });
                    }}
                    className="!min-w-[80px] !bg-indigo-600 hover:!bg-indigo-700 !text-white !font-semibold !normal-case !rounded-xl !shadow-sm disabled:!bg-slate-300 disabled:!text-white"
                  >
                    {copied ? '✓' : '📋'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <CustomizableModal
        modern
        handleHeaderCloseButton={true}
        isOpen={isOpen}
        onClose={handleClose}
        withHeader={true}
        withFooter={false}
        title={getModalTitle()}
        body={getModalBody()}
      />
    </>
  );
};

export default PaymentLinkQRGeneratorModal;
